/**
 * ReportNavBar — Iter-38 Stage 3.
 *
 * Two convenience features that make larger reports easy to move through:
 *
 *   1. Category anchor navigation — a horizontal chip row that scrolls
 *      the user to that category. Each chip shows a status dot so the
 *      "where's the problem?" glance is answered before clicking.
 *
 *   2. Search — a small input that filters issues by label / detail /
 *      URL / reason / category. Search is intentionally SUBORDINATE to
 *      the anchor row: it lives on the right, is muted, and disappears
 *      completely when the user isn't typing. It's a convenience, not
 *      the primary navigation model.
 *
 * The parent (Dashboard.jsx) owns the search query state so the
 * category loop can filter checks against it.
 */
import { useState } from "react";
import { Search, X, ChevronDown } from "lucide-react";

const DOT_CLS = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
};

export default function ReportNavBar({
  categories,
  query,
  onQueryChange,
  activeCategoryKey,
}) {
  const cats = Array.isArray(categories) ? categories : [];
  // Iter-79 — mobile only: the category-chip row is collapsed by
  // default so it doesn't shove the actual report content off-screen.
  // Desktop (≥ md) still shows the full row inline as before.
  const [mobileOpen, setMobileOpen] = useState(false);
  if (!cats.length) return null;
  const activeName = cats.find((c) => (c.key || c.name) === activeCategoryKey)?.name;

  return (
    <div
      className="sticky top-0 z-10 -mx-5 px-5 py-3 mb-4 bg-white/85 backdrop-blur border-b border-black/5"
      data-testid="report-nav-bar"
    >
      {/* Top row: mobile toggle + search  |  desktop chip row + search  */}
      <div className="flex items-center gap-3">
        {/* Mobile-only "Jump to category" toggle */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/10 bg-white text-[11px] font-medium text-charcoal-700 shrink-0"
          aria-expanded={mobileOpen}
          aria-controls="category-jump-nav-mobile"
          data-testid="category-jump-toggle"
        >
          <span className="uppercase tracking-widest text-[10px] text-charcoal-500">Jump</span>
          <span className="text-charcoal-800 truncate max-w-[6rem]">
            {activeName || "category"}
          </span>
          <ChevronDown
            className={`w-3 h-3 transition-transform ${mobileOpen ? "rotate-180" : ""}`}
          />
        </button>

        {/* Desktop chip row (hidden on mobile) */}
        <div
          className="hidden md:flex items-center gap-2 flex-wrap flex-1 min-w-0"
          data-testid="category-jump-nav"
        >
          {cats.map((cat) => {
            const key = cat.key || cat.name;
            const anchor = `#cat-${key}`;
            const active = activeCategoryKey === key;
            return (
              <a
                key={key}
                href={anchor}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                  active
                    ? "bg-charcoal-900 text-white border-charcoal-900"
                    : "bg-white text-charcoal-700 border-black/10 hover:border-charcoal-400"
                }`}
                data-testid={`jump-${key}`}
                aria-label={`Jump to ${cat.name}`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    DOT_CLS[cat.status] || "bg-charcoal-300"
                  }`}
                />
                {cat.name}
              </a>
            );
          })}
        </div>

        <label
          className="relative inline-flex items-center shrink-0 ml-auto md:ml-0"
          data-testid="report-search"
        >
          <Search className="absolute left-2.5 w-3.5 h-3.5 text-charcoal-400 pointer-events-none" />
          <input
            type="search"
            value={query || ""}
            onChange={(e) => onQueryChange?.(e.target.value)}
            placeholder="Search this report…"
            className="pl-8 pr-7 py-1.5 rounded-full text-[12px] w-40 md:w-56 bg-charcoal-50/80 border border-black/5 focus:bg-white focus:border-charcoal-400 focus:outline-none transition-colors"
            data-testid="report-search-input"
            aria-label="Search this report"
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange?.("")}
              className="absolute right-1.5 p-1 text-charcoal-400 hover:text-charcoal-800"
              data-testid="report-search-clear"
              aria-label="Clear search"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </label>
      </div>

      {/* Mobile-only expanded chip row — hidden on desktop */}
      {mobileOpen && (
        <div
          id="category-jump-nav-mobile"
          className="md:hidden mt-3 flex items-center gap-2 flex-wrap"
          data-testid="category-jump-nav-mobile"
        >
          {cats.map((cat) => {
            const key = cat.key || cat.name;
            const anchor = `#cat-${key}`;
            const active = activeCategoryKey === key;
            return (
              <a
                key={key}
                href={anchor}
                onClick={() => setMobileOpen(false)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${
                  active
                    ? "bg-charcoal-900 text-white border-charcoal-900"
                    : "bg-white text-charcoal-700 border-black/10"
                }`}
                data-testid={`jump-mobile-${key}`}
                aria-label={`Jump to ${cat.name}`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    DOT_CLS[cat.status] || "bg-charcoal-300"
                  }`}
                />
                {cat.name}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * checkMatchesQuery — pure helper the Dashboard uses to decide whether
 * a check row should render for the current search query. Case-insensitive
 * substring match across every place a customer might read from:
 * label, detail, category, and each evidence row's url / page_url / reason.
 * Also matches against the legacy items[] fallback shape.
 */
export function checkMatchesQuery(c, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const bag = [
    c?.label,
    c?.detail,
    c?.category,
    c?.severity_label,
    c?.fix_difficulty_label,
  ];
  if (Array.isArray(c?.evidence)) {
    for (const ev of c.evidence) {
      bag.push(ev?.url, ev?.page_url, ev?.reason);
    }
  }
  if (Array.isArray(c?.items)) {
    for (const it of c.items) {
      if (typeof it === "string") bag.push(it);
      else bag.push(it?.url, it?.reason);
    }
  }
  return bag.some((s) => typeof s === "string" && s.toLowerCase().includes(needle));
}
